import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { firmKnowledgeApi } from "../../api/firm-knowledge";
import { ForeignNationalsAPI } from "../../api/foreignNationals";
import { MattersAPI } from "../../api/matters";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useGetUserDetails } from "../../hooks/useGetUserDetails";
import { lawyerSelectors } from "../../redux/lawyer/selectors";
import { Feature as PlanFeature } from "../../types/entitlements";
import type { FirmLibraryEntry, FirmSkill } from "../firmKnowledge/types";
import {
  buildPageResults,
  clientToResult,
  filterPages,
  matterToResult,
  MIN_QUERY_LENGTH,
  noteToResult,
  searchLibrary,
  searchSkills
} from "./searchModel";
import type { SearchResult } from "./types";

const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 6;
const NOTE_LIMIT = 5;
/** The firm's library and skills are loaded whole and reused this long. */
const KNOWLEDGE_TTL_MS = 60_000;

type Knowledge = {
  entries: FirmLibraryEntry[];
  skills: FirmSkill[];
  loadedAt: number;
};

export type GlobalSearchState = {
  query: string;
  setQuery: (value: string) => void;
  /** True while a server round-trip for the current query is in flight. */
  isLoading: boolean;
  /** True when the query is long enough to hit the server. */
  isServerQuery: boolean;
  pages: SearchResult[];
  clients: SearchResult[];
  cases: SearchResult[];
  /** Client notes matching the query, newest first. */
  notes: SearchResult[];
  /** Firm library documents and skills; empty unless `knowledge` was asked for. */
  library: SearchResult[];
  skills: SearchResult[];
  error: string | null;
  reset: () => void;
};

/**
 * Debounced fan-out to the clients and matters list endpoints (both already
 * support `q` and scope to the caller's practice) and the note search (the
 * clients the caller can open), plus a static page list filtered client-side.
 */
export function useGlobalSearch(
  enabled: boolean,
  options?: { knowledge?: boolean }
): GlobalSearchState {
  const wantsKnowledge = Boolean(options?.knowledge);
  const {
    userDetails: { userType, role, visaType, id }
  } = useGetUserDetails();
  const { hasFeature } = useEntitlements();
  useSelector(lawyerSelectors.selectBetaFeatures);

  const hasPlaygroundAccess =
    import.meta.env.REACT_APP_ENABLE_PLAYGROUND === "true" &&
    hasFeature(PlanFeature.ATHENA);
  const isSuperAdmin = role === "superadmin";
  const canSearchRecords = userType === "Lawyer";

  const allPages = useMemo(
    () =>
      buildPageResults({
        userType,
        role,
        uid: id,
        visaLabel: visaType || undefined,
        hasPlaygroundAccess
      }),
    [userType, role, id, visaType, hasPlaygroundAccess]
  );

  const [query, setQuery] = useState("");
  const [clients, setClients] = useState<SearchResult[]>([]);
  const [cases, setCases] = useState<SearchResult[]>([]);
  const [notes, setNotes] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const trimmed = query.trim();
  const isServerQuery =
    enabled && canSearchRecords && trimmed.length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (!isServerQuery) {
      requestId.current += 1;
      setClients([]);
      setCases([]);
      setNotes([]);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    requestId.current += 1;
    const id = requestId.current;
    setIsLoading(true);
    setError(null);

    const timer = window.setTimeout(async () => {
      try {
        const [fnRes, matterRes, noteRes] = await Promise.all([
          ForeignNationalsAPI.list({
            q: trimmed,
            limit: RESULT_LIMIT,
            include_all_practices: isSuperAdmin || undefined
          }),
          MattersAPI.list({ q: trimmed, limit: RESULT_LIMIT }),
          // Notes are extra: their failure leaves clients and cases standing.
          ForeignNationalsAPI.searchNotes(trimmed, NOTE_LIMIT).catch(() => [])
        ]);
        if (id !== requestId.current) return;
        setClients(fnRes.foreign_nationals.map(clientToResult));
        setCases(matterRes.matters.map(matterToResult));
        setNotes(noteRes.map((n) => noteToResult(n, trimmed)));
      } catch (err) {
        if (id !== requestId.current) return;
        setClients([]);
        setCases([]);
        setNotes([]);
        setError(err instanceof Error ? err.message : "Search failed");
      } finally {
        if (id === requestId.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [isServerQuery, trimmed, isSuperAdmin]);

  const [knowledge, setKnowledge] = useState<Knowledge | null>(null);
  useEffect(() => {
    if (!enabled || !wantsKnowledge || !canSearchRecords) return undefined;
    if (knowledge && Date.now() - knowledge.loadedAt < KNOWLEDGE_TTL_MS)
      return undefined;
    let cancelled = false;
    Promise.allSettled([
      firmKnowledgeApi.listLibrary(undefined, "active"),
      firmKnowledgeApi.listSkills(undefined, "all")
    ]).then(([library, skills]) => {
      if (cancelled) return;
      setKnowledge({
        entries: library.status === "fulfilled" ? library.value.entries : [],
        skills: skills.status === "fulfilled" ? skills.value : [],
        loadedAt: Date.now()
      });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, wantsKnowledge, canSearchRecords, knowledge]);

  const library = useMemo(
    () => (knowledge ? searchLibrary(knowledge.entries, query) : []),
    [knowledge, query]
  );
  const skills = useMemo(
    () => (knowledge ? searchSkills(knowledge.skills, query) : []),
    [knowledge, query]
  );

  const pages = useMemo(() => filterPages(allPages, query), [allPages, query]);
  const reset = useCallback(() => setQuery(""), []);

  return {
    query,
    setQuery,
    isLoading,
    isServerQuery,
    pages,
    clients,
    cases,
    notes,
    library,
    skills,
    error,
    reset
  };
}
